import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './App.css';

const API = '';

async function apiAuth(pw) {
  const r = await fetch(`${API}/api/auth`, {
    method:'POST', headers:{'Content-Type':'application/json'},
    body: JSON.stringify({password:pw})
  });
  if(!r.ok) return null;
  return await r.json();
}
async function apiChat(text, sessionId, pw) {
  const r = await fetch(`${API}/api/chat`, {
    method:'POST',
    headers:{'Content-Type':'application/json','X-App-Password':pw},
    body: JSON.stringify({text, session_id: sessionId})
  });
  if(!r.ok) throw new Error(`Chat error ${r.status}`);
  return await r.json();
}
async function apiChatStream(text, sessionId, pw, onEvent) {
  const r = await fetch(`${API}/api/chat/stream`, {
    method:'POST',
    headers:{'Content-Type':'application/json','X-App-Password':pw},
    body: JSON.stringify({text, session_id: sessionId})
  });
  if(!r.ok || !r.body) throw new Error(`Chat stream error ${r.status}`);
  const reader = r.body.getReader();
  const dec = new TextDecoder();
  let buf='';
  while(true){
    const {value, done} = await reader.read();
    if(done) break;
    buf += dec.decode(value, {stream:true});
    let idx;
    while((idx = buf.indexOf('\n\n')) >= 0){
      const chunk = buf.slice(0, idx); buf = buf.slice(idx+2);
      const line = chunk.split('\n').find(l=>l.startsWith('data:'));
      if(!line) continue;
      const js = line.slice(5).trim();
      if(!js) continue;
      try{ onEvent(JSON.parse(js)); }catch(e){}
    }
  }
}
async function apiUpload(file, text, sessionId, pw) {
  const fd = new FormData();
  fd.append('file',file); fd.append('text',text||''); fd.append('session_id',sessionId);
  const r = await fetch(`${API}/api/upload`, {
    method:'POST', headers:{'X-App-Password':pw}, body:fd
  });
  if(!r.ok) throw new Error(`Upload error ${r.status}`);
  return await r.json();
}
async function apiUploadMulti(files, text, sessionId, pw) {
  const fd = new FormData();
  files.forEach(f=>fd.append('file',f));
  fd.append('text',text||''); fd.append('session_id',sessionId);
  const r = await fetch(`${API}/api/upload`, {
    method:'POST', headers:{'X-App-Password':pw}, body:fd
  });
  if(!r.ok) throw new Error(`Upload error ${r.status}`);
  return await r.json();
}
async function apiTTS(text, pw) {
  const r = await fetch(`${API}/api/tts`, {
    method:'POST', headers:{'Content-Type':'application/json','X-App-Password':pw},
    body: JSON.stringify({text})
  });
  if(!r.ok) throw new Error(`TTS error ${r.status}`);
  return await r.blob();
}
async function apiGetHistory(pw) {
  const r = await fetch(`${API}/api/history`, {headers:{'X-App-Password':pw}});
  if(!r.ok) return [];
  const data = await r.json();
  return data.messages || [];
}
async function apiSaveHistory(msgs, pw) {
  await fetch(`${API}/api/history`, {
    method:'POST', headers:{'Content-Type':'application/json','X-App-Password':pw},
    body: JSON.stringify({messages: msgs})
  }).catch(()=>{});
}
async function apiSaveTheme(theme, pw) {
  await fetch(`${API}/api/theme`, {
    method:'POST', headers:{'Content-Type':'application/json','X-App-Password':pw},
    body: JSON.stringify({theme})
  }).catch(()=>{});
}
async function apiGetTheme(pw) {
  try { const r = await fetch(`${API}/api/theme`, {headers:{'X-App-Password':pw}});
    const d = await r.json(); return d.theme; } catch(e){ return null; }
}
async function apiClearHistory(pw) {
  await fetch(`${API}/api/history`, {
    method:'DELETE', headers:{'X-App-Password':pw}
  }).catch(()=>{});
}


async function webauthnHasCreds(pw) {
  try { const headers = pw ? {'X-App-Password':pw} : {}; const r = await fetch(`${API}/api/webauthn/has-credentials`, {headers}); const d = await r.json(); return d.has; } catch(e){ return false; }
}
async function webauthnRegister(pw) {
  const r1 = await fetch(`${API}/api/webauthn/register-options`, {method:'POST', headers:{'X-App-Password':pw}});
  if(!r1.ok) throw new Error('Failed to get options');
  const options = await r1.json();
  options.challenge = base64urlToBuffer(options.challenge);
  options.user.id = base64urlToBuffer(options.user.id);
  if(options.excludeCredentials) options.excludeCredentials.forEach(c=>{ c.id = base64urlToBuffer(c.id); });
  const cred = await navigator.credentials.create({publicKey: options});
  const body = {
    id: cred.id, rawId: bufferToBase64url(cred.rawId), type: cred.type,
    response: {
      attestationObject: bufferToBase64url(cred.response.attestationObject),
      clientDataJSON: bufferToBase64url(cred.response.clientDataJSON),
    }
  };
  const r2 = await fetch(`${API}/api/webauthn/register-verify`, {
    method:'POST', headers:{'Content-Type':'application/json','X-App-Password':pw}, body:JSON.stringify(body)
  });
  if(!r2.ok) { const e = await r2.json(); throw new Error(e.error||'Verify failed'); }
  return true;
}
async function webauthnLogin() {
  const r1 = await fetch(`${API}/api/webauthn/auth-options`, {method:'POST'});
  if(!r1.ok) return null;
  const options = await r1.json();
  options.challenge = base64urlToBuffer(options.challenge);
  if(options.allowCredentials) options.allowCredentials.forEach(c=>{ c.id = base64urlToBuffer(c.id); });
  const cred = await navigator.credentials.get({publicKey: options});
  const body = {
    id: cred.id, rawId: bufferToBase64url(cred.rawId), type: cred.type,
    response: {
      authenticatorData: bufferToBase64url(cred.response.authenticatorData),
      clientDataJSON: bufferToBase64url(cred.response.clientDataJSON),
      signature: bufferToBase64url(cred.response.signature),
      userHandle: cred.response.userHandle ? bufferToBase64url(cred.response.userHandle) : null,
    }
  };
  const r2 = await fetch(`${API}/api/webauthn/auth-verify`, {
    method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(body)
  });
  if(!r2.ok) return null;
  const data = await r2.json();
  return data.password;
}
function base64urlToBuffer(b64) {
  const pad = b64.length%4; const s = b64.replace(/-/g,'+').replace(/_/g,'/') + (pad?'===='.slice(pad):'');
  const bin = atob(s); const buf = new Uint8Array(bin.length);
  for(let i=0;i<bin.length;i++) buf[i]=bin.charCodeAt(i);
  return buf.buffer;
}
function bufferToBase64url(buf) {
  const bytes = new Uint8Array(buf); let s = '';
  bytes.forEach(b=>s+=String.fromCharCode(b));
  return btoa(s).replace(/\+/g,'-').replace(/\//g,'_').replace(/=/g,'');
}

function renderMd(text) {
  if(!text) return '';
  return text
    .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
    .replace(/\*\*(.+?)\*\*/g,'<strong>$1</strong>')
    .replace(/\*(.+?)\*/g,'<em>$1</em>')
    .replace(/`([^`]+)`/g,'<code>$1</code>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,'<a href="$2" target="_blank" rel="noopener">$1</a>')
    .replace(/(^|[^"'>])(https?:\/\/[^\s<>"]+)/g,'$1<a href="$2" target="_blank" rel="noopener">$2</a>')
    .replace(/^### (.+)$/gm,'<b>$1</b>')
    .replace(/^## (.+)$/gm,'<b>$1</b>')
    .replace(/^# (.+)$/gm,'<b>$1</b>')
    .replace(/^[•\-\*] (.+)$/gm,'  · $1')
    .replace(/^\d+\. (.+)$/gm, (m,p1) => {
      const num = m.match(/^(\d+)/)[1];
      return `  ${num}. ${p1}`;
    });
}

function Bubble({text, role, onSpeak}) {
  const [copied, setCopied] = useState(false);
  const html = useMemo(()=> role==='assistant' ? renderMd(text) : null, [text,role]);
  const copy = ()=>{
    if(navigator.clipboard && navigator.clipboard.writeText){
      navigator.clipboard.writeText(text).then(()=>{ setCopied(true); setTimeout(()=>setCopied(false),1500); });
    } else {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position='fixed'; ta.style.opacity='0';
      document.body.appendChild(ta); ta.select(); document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true); setTimeout(()=>setCopied(false),1500);
    }
  };
  return (
    <div className={`msg ${role}`}>
      <div className="bubble" onClick={copy}
        {...(html ? {dangerouslySetInnerHTML:{__html:html}} : {children:text})} />
      {role==='assistant' && text && onSpeak && (
        <button className="speak-btn" title="Озвучить"
          onClick={e=>{e.stopPropagation(); onSpeak(text);}}>
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.6">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
            <path d="M15.5 8.5a5 5 0 010 7"/><path d="M18.5 5.5a9 9 0 010 13"/>
          </svg>
        </button>
      )}
      {copied && <div className="copied-tip">Скопировано</div>}
    </div>
  );
}

function ImageMsg({url, mine}) {
  const src = /^(https?:|blob:|data:)/.test(url) ? url : `${API}/api/image/${encodeURIComponent(url.replace(/^\//,''))}`;
  return (
    <div className={"msg "+(mine?"user":"assistant")}>
      <img className="msg-img" src={src} alt="" loading="lazy" onError={e=>{e.target.style.display='none'}}/>
    </div>
  );
}

function FileMsg({file, pw}) {
  const download = () => {
    fetch(`${API}/api/file/${encodeURIComponent(file.name)}`, {headers:{'X-App-Password':pw}})
      .then(r=>r.blob())
      .then(blob=>{
        const u = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = u; link.download = file.name;
        link.click(); URL.revokeObjectURL(u);
      });
  };
  return (
    <div className="msg assistant">
      <div className="bubble file-bubble" onClick={download}>
        <span className="file-icon">📄</span>
        <span className="file-name">{file.name}</span>
        <span className="file-dl">⬇</span>
      </div>
    </div>
  );
}

function Login({onLogin}) {
  const [pw,setPw]=useState('');
  const [err,setErr]=useState('');
  const [loading,setLoading]=useState(false);
  const [hasBio,setHasBio]=useState(false);

  useEffect(()=>{
    webauthnHasCreds().then(has=>{
      setHasBio(has);
      if(has) {
        setLoading(true);
        webauthnLogin().then(async p=>{
          if(p) {
            const res = await apiAuth(p);
            onLogin(p, res?.assistant_name, res?.user);
          } else setLoading(false);
        }).catch(()=>setLoading(false));
      }
    });
  },[]);

  const submit = async e => {
    e.preventDefault(); setLoading(true); setErr('');
    const res = await apiAuth(pw);
    if(res) onLogin(pw, res.assistant_name, res.user);
    else { setErr('Неверный пароль'); setLoading(false); }
  };

  const bioLogin = async () => {
    setLoading(true); setErr('');
    try {
      const p = await webauthnLogin();
      if(p) {
        const res = await apiAuth(p);
        onLogin(p, res?.assistant_name);
      }
      else { setErr('Не удалось войти'); setLoading(false); }
    } catch(e) { setErr('Ошибка биометрии'); setLoading(false); }
  };

  return (
    <div className="login"><div className="login-card">
      <div className="login-avatar"><svg viewBox="0 0 60 60" fill="none">
        <circle cx="30" cy="30" r="28" stroke="currentColor" strokeWidth="1" opacity=".25"/>
        <circle cx="30" cy="30" r="18" stroke="currentColor" strokeWidth="1" opacity=".45"/>
        <circle cx="30" cy="30" r="8" fill="currentColor" opacity=".7"/>
      </svg></div>
      <h1>Анна</h1><p className="login-hint">Персональный ассистент</p>
      {hasBio && (
        <button className="bio-btn" onClick={bioLogin} disabled={loading}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="28" height="28">
            <path d="M12 11c0-1.1.9-2 2-2s2 .9 2 2c0 1.5-1 2.5-2 3.5"/>
            <path d="M8 15c0-2.2 1.8-4 4-4"/>
            <path d="M16 15a4 4 0 00-8 0"/>
            <path d="M6 13a6 6 0 0112 0v2"/>
            <path d="M4 15a8 8 0 0116 0"/>
            <rect x="3" y="3" width="18" height="18" rx="4"/>
          </svg>
          <span>Войти по отпечатку</span>
        </button>
      )}
      <form onSubmit={submit}>
        <input type="password" value={pw} onChange={e=>setPw(e.target.value)}
          placeholder="Пароль" autoFocus={!hasBio} disabled={loading}
            autoComplete="off" data-lpignore="true" data-form-type="other"/>
        <button type="submit" disabled={loading||!pw}>{loading?'...':'Войти'}</button>
      </form>
      {err&&<p className="login-err">{err}</p>}
    </div></div>
  );
}

function App() {
  const [pw,setPw]=useState(()=>sessionStorage.getItem('anna_pw')||'');
  const [authed,setAuthed]=useState(false);
  const [msgs,setMsgs]=useState([]);
  const [input,setInput]=useState('');
  const [listening,setListening]=useState(false);
  const [thinking,setThinking]=useState(false);
  const [speaking,setSpeaking]=useState(false);
  const [interim,setInterim]=useState('');
  const [ttsOn,setTtsOn]=useState(false);
  const [pendingFiles,setPendingFiles]=useState([]);
  const [voiceMode,setVoiceMode]=useState(false);
  const [recording,setRecording]=useState(false);
  const mediaRecRef=useRef(null);
  const chunksRef=useRef([]);
  const voiceModeRef=useRef(false);
  const [loaded,setLoaded]=useState(false);
  const [currentUser,setCurrentUser]=useState('maxim');
  const [hasBioRegistered,setHasBioRegistered]=useState(false);
  const [showSettings,setShowSettings]=useState(false);
  const [usage,setUsage]=useState(null);
  const [theme,setTheme]=useState(()=>{
    try { return JSON.parse(localStorage.getItem('anna_theme')||'null') } catch(e){ return null }
  });
  const [assistantName,setAssistantName]=useState('Анна');

  const endRef=useRef(null);
  const scrollRef=useRef(null);
  const recRef=useRef(null);
  const audioRef=useRef(null);
  const sendRef=useRef(null);
  const fileRef=useRef(null);
  const imgRef=useRef(null);
  const inputRef=useRef(null);
  const sessionId=useRef(`s${Date.now()}`);
  const saveTimer=useRef(null);

  // Auth + load history from server
  useEffect(()=>{
    if(pw) apiAuth(pw).then(res=>{
      if(res) {
        setAuthed(true);
        if(res.user) setCurrentUser(res.user);
        if(res.assistant_name) setAssistantName(res.assistant_name);
        webauthnHasCreds(pw).then(setHasBioRegistered);
        apiGetHistory(pw).then(h=>{ if(h.length) setMsgs(h); setLoaded(true); });
        apiGetTheme(pw).then(t=>{ if(t) { setTheme(t); localStorage.setItem('anna_theme',JSON.stringify(t)); } });
      } else {
        sessionStorage.removeItem('anna_pw');
        setLoaded(true);
      }
    });
  },[]);

  const login=(p,name,user)=>{ setPw(p); sessionStorage.setItem('anna_pw',p); setAuthed(true);
    if(name) setAssistantName(name);
    if(user) setCurrentUser(user);
    apiGetHistory(p).then(h=>{ if(h.length) setMsgs(h); setLoaded(true); });
  };

  // Scroll to bottom
  const refreshUsage = useCallback(()=>{
    if(currentUser==='maxim') fetch(`${API}/api/usage`,{headers:{'X-App-Password':pw}})
      .then(r=>r.json()).then(setUsage).catch(()=>{});
  },[pw,currentUser]);
  useEffect(()=>{ if(authed) refreshUsage(); },[authed,refreshUsage]);
  useEffect(()=>{ if(authed && msgs.length>0) refreshUsage(); },[msgs.length]);
  useEffect(()=>{ document.title = assistantName; },[assistantName]);
  useEffect(()=>{
    if(!theme) return;
    const r = document.documentElement;
    if(theme.bg) r.style.setProperty('--bg', theme.bg);
    if(theme.card) r.style.setProperty('--card', theme.card);
    if(theme.bubUser) r.style.setProperty('--bub-u', theme.bubUser);
    if(theme.bubAi) r.style.setProperty('--bub-a', theme.bubAi);
    if(theme.acc) r.style.setProperty('--acc', theme.acc);
    if(theme.txt) r.style.setProperty('--txt', theme.txt);
  },[theme]);
  const saveTheme = (t)=>{ setTheme(t); localStorage.setItem('anna_theme',JSON.stringify(t));
    apiSaveTheme(t, pw); };
  const resetTheme = ()=>{ setTheme(null); localStorage.removeItem('anna_theme');
    const r=document.documentElement;
    ['--bg','--card','--bub-u','--bub-a','--acc','--txt'].forEach(v=>r.style.removeProperty(v));
    apiSaveTheme(null, pw); };
  useEffect(()=>{
    const el=scrollRef.current;
    if(!el) return;
    // авто-скролл только если пользователь у низа ленты; проскроллил вверх — не якорим
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 140;
    if(nearBottom) endRef.current?.scrollIntoView({behavior:'smooth'});
  },[msgs,interim]);

  // Save history to server (debounced)
  useEffect(()=>{
    if(!loaded || !authed) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(()=>{ apiSaveHistory(msgs, pw); }, 1000);
  },[msgs, loaded, authed, pw]);



  const autoResize = useCallback(()=>{
    const el = inputRef.current;
    if(el){ el.style.height='auto'; el.style.height=Math.min(el.scrollHeight,120)+'px'; }
  },[]);

  const playTTS = useCallback(async(text,force)=>{
    if((!ttsOn&&!force)||!text) return;
    try{
      setSpeaking(true);
      const blob=await apiTTS(text,pw);
      const url=URL.createObjectURL(blob);
      const a=new Audio(url);
      audioRef.current=a;
      a.onended=()=>{setSpeaking(false);audioRef.current=null;URL.revokeObjectURL(url);
        if(voiceModeRef.current){
          setTimeout(()=>{
            const SR=window.SpeechRecognition||window.webkitSpeechRecognition;
            if(!SR) return;
            const rec=new SR(); rec.lang='ru-RU'; rec.interimResults=true; rec.continuous=false;
            rec.onresult=e=>{
              let fin='',int='';
              for(let i=e.resultIndex;i<e.results.length;i++){
                const t=e.results[i][0].transcript;
                if(e.results[i].isFinal)fin+=t; else int+=t;
              }
              setInterim(int);
              if(fin){setInterim('');sendRef.current?.(fin.trim());}
            };
            rec.onerror=()=>{setListening(false);setInterim('');};
            rec.onend=()=>{setListening(false);setInterim('');};
            recRef.current=rec; rec.start(); setListening(true);
          },300);
        }
      };
      a.onerror=()=>{setSpeaking(false);audioRef.current=null;};
      await a.play();
    }catch(e){console.error('TTS:',e);setSpeaking(false);}
  },[pw,ttsOn]);

  const notify = useCallback(()=>{
    if(document.hidden){
      try{
        const ctx=new(window.AudioContext||window.webkitAudioContext)();
        const o=ctx.createOscillator(); const g=ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.frequency.value=880; g.gain.value=0.1;
        o.start(); o.stop(ctx.currentTime+0.15);
      }catch(e){}
    }
  },[]);

  const processResponse = useCallback((data, addMsgs) => {
    const newMsgs = [];
    if(data.reply) newMsgs.push({role:'assistant', text:data.reply});
    if(data.extra_messages) {
      data.extra_messages.forEach(m => { if(m.trim()) newMsgs.push({role:'assistant', text:m}); });
    }
    if(data.image_url && !data.image_url.startsWith('DIRECT:') && !data.image_url.startsWith('MULTI:')) {
      newMsgs.push({role:'image', url:data.image_url});
    }
    if(data.files && data.files.length > 0) {
      data.files.forEach(f => { newMsgs.push({role:'file', file:f}); });
    }
    addMsgs(newMsgs);
  },[]);

  const budgetCheck = useCallback(async()=>{
    if(currentUser!=='maxim') return;
    try {
      const u = await fetch(`${API}/api/usage`,{headers:{'X-App-Password':pw}}).then(r=>r.json());
      if(u && u.month && u.month.budget - u.month.cost < 1) {
        setMsgs(p=>[...p,{role:'assistant',text:'⚠️ Внимание! На Claude осталось меньше $1. Пополни счёт, иначе скоро перестану работать.'}]);
      }
    } catch(e){}
  },[pw,currentUser]);

  const send = useCallback(async txt=>{
    if(!txt||thinking) return;
    setMsgs(p=>[...p,{role:'user',text:txt}]);
    setInput(''); setThinking(true);
    if(inputRef.current) inputRef.current.style.height='auto';

    // user2 (жена) — старый нестриминговый путь
    if(currentUser!=='maxim'){
      try{
        const data=await apiChat(txt,sessionId.current,pw);
        processResponse(data, newMsgs => setMsgs(p=>[...p,...newMsgs]));
        setThinking(false); notify(); await playTTS(data.reply);
      }catch(e){
        console.error(e);
        setMsgs(p=>[...p,{role:'assistant',text:'Ошибка. Попробуй ещё раз.'}]);
        setThinking(false);
      }
      return;
    }

    // maxim — стриминг
    let acc=''; let gotDelta=false;
    try{
      await apiChatStream(txt, sessionId.current, pw, ev=>{
        if(ev.type==='delta'){
          acc += ev.text;
          setMsgs(p=>{
            const c=[...p];
            const last=c[c.length-1];
            // решаем по содержимому массива, а не по внешнему флагу —
            // иначе React-батчинг перезаписывает сообщение пользователя
            if(last && last.role==='assistant' && last.streaming){
              c[c.length-1]={...last,text:acc};
            } else {
              c.push({role:'assistant',text:acc,streaming:true});
            }
            return c;
          });
          gotDelta=true;
        } else if(ev.type==='done'){
          // снимаем служебный флаг streaming с финального пузыря
          setMsgs(p=>{ const c=[...p]; const l=c[c.length-1]; if(l&&l.streaming){ c[c.length-1]={role:l.role,text:l.text}; } return c; });
          const extra=[];
          if(ev.extra_messages) ev.extra_messages.forEach(m=>{ if(m&&m.trim()) extra.push({role:'assistant',text:m}); });
          if(ev.image_url && !ev.image_url.startsWith('DIRECT:') && !ev.image_url.startsWith('MULTI:')) extra.push({role:'image',url:ev.image_url});
          if(ev.files) ev.files.forEach(f=>extra.push({role:'file',file:f}));
          if(extra.length) setMsgs(p=>[...p,...extra]);
        } else if(ev.type==='error'){
          setMsgs(p=>[...p,{role:'assistant',text:'Ошибка. Попробуй ещё раз.'}]);
        }
      });
      setThinking(false); notify();
      await budgetCheck();
      if(acc) await playTTS(acc);
    }catch(e){
      console.error(e);
      // фолбэк на нестриминговый путь, если стрим не стартовал
      if(!gotDelta){
        try{
          const data=await apiChat(txt,sessionId.current,pw);
          processResponse(data, newMsgs => setMsgs(p=>[...p,...newMsgs]));
          setThinking(false); notify(); await budgetCheck(); await playTTS(data.reply);
          return;
        }catch(e2){ console.error(e2); }
      }
      setMsgs(p=>[...p,{role:'assistant',text:'Ошибка. Попробуй ещё раз.'}]);
      setThinking(false);
    }
  },[pw,thinking,playTTS,notify,processResponse,currentUser,budgetCheck]);

  const sendFile = useCallback(async(file,text)=>{
    if(thinking) return;
    const label=file.name||'файл';
    const isAudio=/\.(ogg|mp3|wav|m4a|webm|oga)$/i.test(label);
    const isImage=(file.type||'').startsWith('image/')||/\.(jpe?g|png|gif|webp|bmp|heic|heif)$/i.test(label);
    if(isImage){
      const localUrl=URL.createObjectURL(file);
      setMsgs(p=>[...p,{role:'image',url:localUrl,mine:true},...(text?[{role:'user',text}]:[])]);
    }else{
      setMsgs(p=>[...p,{role:'user',text:isAudio?'🎤 Голосовое':`📎 ${label}${text?'\n'+text:''}`}]);
    }
    setThinking(true);
    try{
      const data=await apiUpload(file,text,sessionId.current,pw);
      if(data.recognized){
        setMsgs(p=>{const c=[...p]; c[c.length-1]={...c[c.length-1],text:`🎤 ${data.recognized}`}; return c;});
      }
      processResponse(data, newMsgs => setMsgs(p=>[...p,...newMsgs]));
      setThinking(false);
      notify();
      await playTTS(data.reply);
    }catch(e){
      console.error(e);
      setMsgs(p=>[...p,{role:'assistant',text:'Ошибка при обработке файла.'}]);
      setThinking(false);
    }
  },[pw,thinking,playTTS,notify,processResponse]);

  const sendFiles = useCallback(async(files,text)=>{
    if(thinking||!files.length) return;
    const bubbles=files.map(f=>({role:'image',url:URL.createObjectURL(f),mine:true}));
    if(text) bubbles.push({role:'user',text});
    setMsgs(p=>[...p,...bubbles]);
    setThinking(true);
    try{
      const data=await apiUploadMulti(files,text,sessionId.current,pw);
      processResponse(data, newMsgs => setMsgs(p=>[...p,...newMsgs]));
      setThinking(false); notify();
      await playTTS(data.reply);
    }catch(e){
      console.error(e);
      setMsgs(p=>[...p,{role:'assistant',text:'Ошибка при обработке фото.'}]);
      setThinking(false);
    }
  },[pw,thinking,playTTS,notify,processResponse]);

  useEffect(()=>{sendRef.current=send;},[send]);

  const handleFileSelect=useCallback(e=>{
    const file=e.target.files?.[0]; if(!file) return; e.target.value='';
    if(/\.(ogg|mp3|wav|m4a|webm|oga)$/i.test(file.name)) sendFile(file,'');
    else setPendingFiles([file]); // документ — один, заменяет вложения
  },[sendFile]);

  const handleImageSelect=useCallback(e=>{
    const files=[...(e.target.files||[])]; if(!files.length) return; e.target.value='';
    setPendingFiles(prev=>[...prev.filter(f=>(f.type||'').startsWith('image/')), ...files]);
  },[]);

  const handlePaste=useCallback(e=>{
    const items=e.clipboardData?.items; if(!items) return;
    for(const it of items){
      if(it.type&&it.type.startsWith('image/')){
        const blob=it.getAsFile();
        if(blob){
          e.preventDefault();
          const ext=(it.type.split('/')[1]||'png').replace('jpeg','jpg');
          const f=new File([blob],`paste_${Date.now()}.${ext}`,{type:it.type});
          setPendingFiles(prev=>[...prev.filter(x=>(x.type||'').startsWith('image/')), f]);
        }
        break;
      }
    }
  },[]);

  const sendPending=useCallback(()=>{
    if(!pendingFiles.length) return;
    const files=pendingFiles, text=input;
    setPendingFiles([]); setInput('');
    if(files.length===1) sendFile(files[0],text);
    else sendFiles(files,text);
  },[pendingFiles,input,sendFile,sendFiles]);

  const pendingUrls=useMemo(()=> pendingFiles.map(f=>(f.type||'').startsWith('image/')?URL.createObjectURL(f):null),[pendingFiles]);
  useEffect(()=>()=>{ pendingUrls.forEach(u=>{ if(u) URL.revokeObjectURL(u); }); },[pendingUrls]);
  const removePending=useCallback(idx=>setPendingFiles(prev=>prev.filter((_,i)=>i!==idx)),[]);

  const startRecording = useCallback(async ()=>{
    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio:true});
      const mr = new MediaRecorder(stream, {mimeType:'audio/webm;codecs=opus'});
      chunksRef.current = [];
      mr.ondataavailable = e => { if(e.data.size>0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t=>t.stop());
        const blob = new Blob(chunksRef.current, {type:'audio/webm'});
        if(blob.size < 1000) return; // слишком короткая запись
        const file = new File([blob], 'voice.webm', {type:'audio/webm'});
        setRecording(false);
        await sendFile(file, '');
      };
      mediaRecRef.current = mr;
      mr.start();
      setRecording(true);
    } catch(e) {
      console.error('Mic error:', e);
      alert('Нет доступа к микрофону');
    }
  },[sendFile]);

  const stopRecording = useCallback(()=>{
    if(mediaRecRef.current && mediaRecRef.current.state==='recording'){
      mediaRecRef.current.stop();
    }
  },[]);

  const dialogAccRef=useRef('');
  const dialogTimerRef=useRef(null);

  function createRecognition(){
    const SR=window.SpeechRecognition||window.webkitSpeechRecognition;
    if(!SR) return null;
    const r=new SR(); r.lang='ru-RU'; r.interimResults=true; r.continuous=false;
    r.onresult=e=>{
      let fin='',int='';
      for(let i=e.resultIndex;i<e.results.length;i++){
        const t=e.results[i][0].transcript;
        if(e.results[i].isFinal)fin+=t; else int+=t;
      }
      if(voiceModeRef.current){
        if(fin) dialogAccRef.current += fin;
        setInterim(dialogAccRef.current + int);
        clearTimeout(dialogTimerRef.current);
      } else {
        setInterim(int);
        if(fin){setInterim('');sendRef.current?.(fin.trim());}
      }
    };
    r.onerror=()=>{
      if(!voiceModeRef.current){setListening(false);setInterim('');}
    };
    r.onend=()=>{
      if(voiceModeRef.current){
        clearTimeout(dialogTimerRef.current);
        dialogTimerRef.current = setTimeout(()=>{
          const text = dialogAccRef.current.trim();
          if(text){
            dialogAccRef.current = '';
            setInterim('');
            setListening(false);
            sendRef.current?.(text);
          }
        }, 3000);
        // Перезапускаем слушание пока ждём
        try{
          const r2 = createRecognition();
          if(r2){recRef.current=r2; r2.start();}
        }catch(e){}
      } else {
        setListening(false);
        setInterim('');
      }
    };
    return r;
  }

  const initRec=useCallback(()=>{
    return createRecognition();
  },[]);

  const toggleMic=useCallback(()=>{
    if(thinking||speaking) return;
    if(listening){recRef.current?.stop();setListening(false);return;}
    if(audioRef.current){audioRef.current.pause();audioRef.current=null;setSpeaking(false);}
    const r=initRec();
    if(!r){alert('Браузер не поддерживает распознавание речи');return;}
    recRef.current=r; r.start(); setListening(true);
  },[listening,thinking,speaking,initRec]);

  const submit=e=>{
    e.preventDefault();
    if(pendingFiles.length){sendPending();return;}
    if(input.trim()) send(input.trim());
  };
  const stopAudio=()=>{if(audioRef.current){audioRef.current.pause();audioRef.current=null;setSpeaking(false);}};
  const clear=()=>{stopAudio();setMsgs([]);setPendingFiles([]);sessionId.current=`s${Date.now()}`;apiClearHistory(pw);};

  if(!authed) return <Login onLogin={login}/>;
  const state=thinking?'think':speaking?'speak':listening?'listen':'idle';

  return (
    <div className="app">
      <header className="hdr">
        <div className="hdr-l"><div className="hdr-dot"/><span>{assistantName}</span></div>
        <div className="hdr-r">
          {currentUser!=='maxim' && !hasBioRegistered && (
            <button className="ib bio-pulse" onClick={async()=>{
              try{ await webauthnRegister(pw); alert('Face ID / отпечаток привязан! Теперь при следующем входе не нужен пароль.'); setHasBioRegistered(true); }catch(e){ alert('Ошибка: '+e.message); }
            }} title="Привязать Face ID">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 11c0-1.1.9-2 2-2s2 .9 2 2c0 1.5-1 2.5-2 3.5"/>
                <path d="M8 15c0-2.2 1.8-4 4-4"/><path d="M16 15a4 4 0 00-8 0"/>
                <path d="M6 13a6 6 0 0112 0v2"/><path d="M4 15a8 8 0 0116 0"/>
              </svg>
            </button>
          )}
          <button className="ib" onClick={()=>send('Что ты умеешь?')} title="Что я умею">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10"/><path d="M9 9a3 3 0 115.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </button>
          <button className="ib" onClick={()=>setShowSettings(!showSettings)} title="Настройки">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M12.22 2h-.44a2 2 0 00-2 2v.18a2 2 0 01-1 1.73l-.43.25a2 2 0 01-2 0l-.15-.08a2 2 0 00-2.73.73l-.22.38a2 2 0 00.73 2.73l.15.1a2 2 0 011 1.72v.51a2 2 0 01-1 1.74l-.15.09a2 2 0 00-.73 2.73l.22.38a2 2 0 002.73.73l.15-.08a2 2 0 012 0l.43.25a2 2 0 011 1.73V20a2 2 0 002 2h.44a2 2 0 002-2v-.18a2 2 0 011-1.73l.43-.25a2 2 0 012 0l.15.08a2 2 0 002.73-.73l.22-.39a2 2 0 00-.73-2.73l-.15-.08a2 2 0 01-1-1.74v-.5a2 2 0 011-1.74l.15-.09a2 2 0 00.73-2.73l-.22-.38a2 2 0 00-2.73-.73l-.15.08a2 2 0 01-2 0l-.43-.25a2 2 0 01-1-1.73V4a2 2 0 00-2-2z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
          <button className={`ib ${ttsOn?'on':''}`} onClick={()=>setTtsOn(!ttsOn)} title="Озвучка">
            {ttsOn?(<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M19 4.9a10 10 0 010 14.2M15.5 8.5a5 5 0 010 7"/>
            </svg>):(<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M11 5L6 9H2v6h4l5 4V5z"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/>
            </svg>)}
          </button>
          <button className="ib" onClick={clear} title="Очистить">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
            </svg>
          </button>
        </div>
      </header>


      {showSettings && (
        <div className="settings-panel">
          <div className="settings-title">Оформление</div>
          <div className="settings-grid">
            <div className="settings-item">
              <label>Фон</label>
              <input type="color" value={(theme&&theme.bg)||'#08080d'}
                onChange={e=>saveTheme({...theme,bg:e.target.value})}/>
            </div>
            <div className="settings-item">
              <label>Карточки</label>
              <input type="color" value={(theme&&theme.card)||'#111118'}
                onChange={e=>saveTheme({...theme,card:e.target.value})}/>
            </div>
            <div className="settings-item">
              <label>Мои сообщения</label>
              <input type="color" value={(theme&&theme.bubUser)||'#1a1a2e'}
                onChange={e=>saveTheme({...theme,bubUser:e.target.value})}/>
            </div>
            <div className="settings-item">
              <label>Ответы</label>
              <input type="color" value={(theme&&theme.bubAi)||'#0f0f1a'}
                onChange={e=>saveTheme({...theme,bubAi:e.target.value})}/>
            </div>
            <div className="settings-item">
              <label>Акцент</label>
              <input type="color" value={(theme&&theme.acc)||'#6c5ce7'}
                onChange={e=>saveTheme({...theme,acc:e.target.value})}/>
            </div>
            <div className="settings-item">
              <label>Текст</label>
              <input type="color" value={(theme&&theme.txt)||'#e4e4ef'}
                onChange={e=>saveTheme({...theme,txt:e.target.value})}/>
            </div>
          </div>
          <div className="settings-presets">
            <button onClick={()=>saveTheme({bg:'#08080d',card:'#111118',bubUser:'#1a1a2e',bubAi:'#0f0f1a',acc:'#6c5ce7',txt:'#e4e4ef'})}>Тёмная</button>
            <button onClick={()=>saveTheme({bg:'#1a1520',card:'#241e2e',bubUser:'#2d1f3d',bubAi:'#1e1528',acc:'#9b59b6',txt:'#e8dff0'})}>Фиолет</button>
            <button onClick={()=>saveTheme({bg:'#0d1117',card:'#161b22',bubUser:'#1c2333',bubAi:'#12181f',acc:'#58a6ff',txt:'#c9d1d9'})}>Синяя</button>
            <button onClick={()=>saveTheme({bg:'#0a120a',card:'#111a11',bubUser:'#1a2e1a',bubAi:'#0f1a0f',acc:'#2ecc71',txt:'#d4edda'})}>Зелёная</button>
            <button onClick={()=>saveTheme({bg:'#f5f5f0',card:'#ffffff',bubUser:'#e3e0f7',bubAi:'#f0f0f0',acc:'#6c5ce7',txt:'#1a1a2e'})}>Светлая</button>
            <button onClick={resetTheme}>Сброс</button>
          </div>
        </div>
      )}
      <div className="msgs" ref={scrollRef}>
        {msgs.length===0&&!interim&&(
          <div className="empty">
            <div className="rings"><div className="rn r1"/><div className="rn r2"/><div className="rn r3"/></div>
            <p>Нажми на микрофон<br/>или напиши сообщение</p>
            <div className="quick-hints">
              <button onClick={()=>send('Что ты умеешь?')}>Что ты умеешь?</button>
              {currentUser==='maxim' && <button onClick={()=>send('План на сегодня')}>План на день</button>}
              <button onClick={()=>send('Какая погода в Москве?')}>Погода</button>
            </div>
          </div>
        )}
        {msgs.map((m,i)=>{
          if(m.role==='image') return <ImageMsg key={i} url={m.url} mine={m.mine}/>;
          if(m.role==='file') return <FileMsg key={i} file={m.file} pw={pw}/>;
          return <Bubble key={i} text={m.text} role={m.role} onSpeak={t=>playTTS(t,true)}/>;
        })}
        {interim&&<div className="msg user dim"><div className="bubble">{interim}...</div></div>}
        {thinking&&<div className="msg assistant"><div className="bubble dots"><span/><span/><span/></div></div>}
        <div ref={endRef}/>
      </div>

      <div className="ctl">
        <div className="mic-wrap">
          {(listening||speaking)&&<><div className={`pulse p1 ${state}`}/><div className={`pulse p2 ${state}`}/></>}
          <button className={`mic ${state} ${voiceMode?'vm':''} ${recording?'rec':''}`}
            onPointerDown={(e)=>{
              if(thinking) return;
              if(speaking){stopAudio();return;}
              if(voiceMode){
                setVoiceMode(false);voiceModeRef.current=false;
                clearTimeout(dialogTimerRef.current);
                dialogAccRef.current='';
                recRef.current?.stop();setListening(false);setInterim('');setTtsOn(false);
                return;
              }
              // Запоминаем время нажатия для определения hold vs tap
              e.currentTarget.dataset.downTime = Date.now();
            }}
            onPointerUp={(e)=>{
              if(thinking||speaking||voiceMode) return;
              const downTime = parseInt(e.currentTarget.dataset.downTime||'0');
              const held = Date.now() - downTime;
              if(recording){
                // Отпустил после удержания — отправить запись
                stopRecording();
              } else if(held < 300){
                // Короткое нажатие — начать запись (нажмёт ещё раз чтобы отправить)
                if(recording){stopRecording();}
                else{startRecording();}
              }
            }}
            onDoubleClick={(e)=>{
              e.preventDefault();
              if(thinking) return;
              if(recording){stopRecording();setRecording(false);}
              setVoiceMode(true);voiceModeRef.current=true;setTtsOn(true);
              dialogAccRef.current='';
              const r=createRecognition();
              if(r){recRef.current=r;r.start();setListening(true);}
            }}
            disabled={thinking}>
            {thinking?(<svg className="spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2a10 10 0 0110 10" strokeLinecap="round"/>
            </svg>):speaking?(<svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>
            ):recording?(<svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>
            ):(<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10a7 7 0 0014 0"/>
              <line x1="12" y1="17" x2="12" y2="22"/><line x1="8" y1="22" x2="16" y2="22"/>
            </svg>)}
          </button>
        </div>
        <div className="status">
          {voiceMode&&!listening&&!thinking&&!speaking&&'🔴 Режим диалога'}{recording&&'🔴 Записываю...'}{listening&&(voiceMode?'🔴 Слушаю...':'Слушаю...')}{thinking&&'Думаю...'}{speaking&&'Говорю...'}
        </div>
        {pendingFiles.length>0&&(
          pendingUrls.some(Boolean)
            ? <div className="pending-thumbs">
                {pendingFiles.map((f,i)=>(
                  <div className="thumb-wrap" key={i}>
                    {pendingUrls[i]
                      ? <img className="pending-thumb" src={pendingUrls[i]} alt=""/>
                      : <span className="thumb-doc">📎</span>}
                    <button className="thumb-x" onClick={()=>removePending(i)}>&times;</button>
                  </div>
                ))}
              </div>
            : <div className="file-badge">
                <span>📎 {pendingFiles[0].name}</span>
                <button onClick={()=>removePending(0)}>&times;</button>
              </div>
        )}
        <form className="inp" onSubmit={submit}>
          <input type="file" ref={fileRef} onChange={handleFileSelect} style={{display:'none'}}
            accept="audio/*,.pdf,.xlsx,.xls,.csv,.docx,.doc"/>
          <input type="file" ref={imgRef} onChange={handleImageSelect} style={{display:'none'}}
            accept="image/*" multiple/>
          <button type="button" className="attach-btn" onClick={()=>imgRef.current?.click()} disabled={thinking} title="Фото из галереи">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8.5" cy="8.5" r="1.8"/>
              <path d="M21 15l-5-5L5 21"/>
            </svg>
          </button>
          <button type="button" className="attach-btn" onClick={()=>fileRef.current?.click()} disabled={thinking} title="Документ">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
            </svg>
          </button>
          <textarea ref={inputRef} value={input} onChange={e=>{setInput(e.target.value);autoResize();}}
            onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();submit(e);}}}
            onPaste={handlePaste}
            placeholder={pendingFiles.length?(pendingUrls.some(Boolean)?"Комментарий к фото...":"Комментарий к файлу..."):"Сообщение..."} disabled={thinking||listening}
            rows="1"/>
          <button type="submit" disabled={(!input.trim()&&!pendingFiles.length)||thinking}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
}

export default App;
